import React, { useEffect, useState } from 'react';
import {
  Box, Dialog, Grid, IconButton, Paper, TextField, Typography,
} from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import moment, { Moment } from 'moment';
import clonedeep from 'lodash.clonedeep';
import { WithStyles } from '@material-ui/styles';
import CodeIcon from '@material-ui/icons/VpnKey';
import InfoIcon from '@material-ui/icons/LocalOffer';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import ClockIcon from '@material-ui/icons/AccessTime';
import CloseIcon from '@material-ui/icons/Close';
import styles from './styles';
import CustomButton from '../CustomButton';
import { ScheduledCode } from '../../redux/types/hubs';
import { getFreeSlot } from './utils';
import { PropertyDetailsInfo } from '../../redux/types/properties';
import { beautifyErrors, ValidationErrors } from '../../utils/helpers';
import { lockScheduledCodeSchema } from './validation';
import { usePermissions } from "../../hooks/usePermissions";


const orange = {
  main: "#DB5C0E",
  light: "#F4D18C",
};

const pickerTheme = createMuiTheme({
  palette: { primary: { main: orange.main } },
  overrides: {
    MuiPickersToolbar: { toolbar: { backgroundColor: orange.main } },
    MuiPickersDay: {
      daySelected: {
        backgroundColor: orange.main,
        "&:hover": { backgroundColor: orange.main },
      },
      current: { color: orange.main },
    },
    MuiPickersCalendarHeader: {
      switchHeader: { "& button": { color: orange.main } },
    },
    MuiPickersClock: { pin: { backgroundColor: orange.main } },
    MuiPickersClockPointer: { pointer: { backgroundColor: orange.main } },
    MuiPickersClockNumber: {
      clockNumberSelected: {
        color: orange.light,
        backgroundColor: orange.main,
      },
    },
    MuiTabs: { indicator: { backgroundColor: "#FFFFFF" } },
    MuiTab: {
      textColorInherit: {
        "&$selected": { color: "#FFFFFF" },
        color: "#8E8B99",
      },
    },
  },
} as any);

type Props = {
  scheduledCodes: ScheduledCode[];
  property: PropertyDetailsInfo;
  updating: boolean;
  deleteHubScheduledCode: (slot: number) => void;
  updateHubScheduledCodes: (payload: ScheduledCode[]) => void;
} & WithStyles<typeof styles>;

type NewCode = {
  name: string;
  code: string;
  scheduled_on: string;
  expires_on: string;
};

const HubScheduledCodesForm: React.FC<Props> = ({
  classes,
  scheduledCodes,
  updating,
  property,
  deleteHubScheduledCode,
  updateHubScheduledCodes,
  }) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [newCode, setNewCode] = useState<NewCode | null>(null);
  const [scheduledCodesArr, setScheduledCodesArr] = useState<ScheduledCode[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrors<NewCode>
  >({});
  const { canEdit } = usePermissions();

  useEffect(() => {
    setScheduledCodesArr(scheduledCodes ?? []);
  }, [scheduledCodes]);

  const deleteScheduledCode = (slot: number) => {
    if (!canEdit) return;
    deleteHubScheduledCode(slot);
  };

  const formatScheduledDate = (isoString: string): string => {
    const date = new Date(isoString);

    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const time = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    function getOrdinal(n: number) {
      if (n > 3 && n < 21) return 'th';
      switch (n % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    }

    return `${month} ${day}${getOrdinal(day)} ${time}`;
  };

  const addNewCodeButton = () => (
    <CustomButton
      variant="orange"
      disabled={updating || !canEdit}
      onClick={() => {
        setOpenModal(true);
        setNewCode({
          code: '',
          name: '',
          expires_on: '',
          scheduled_on: '',
        });
      }}
      style={{
        width: '30%',
      }}
    >
      Add New Scheduled Code
    </CustomButton>
  );

  const handleNewCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!newCode || (name === 'code' && Number.isNaN(Number(value)))) {
      return;
    }

    setNewCode({ ...newCode, [name]: value });
  };

  const handleDateChange = (name: string, value: Moment | Date | null) => {
    if (!value || !newCode) {
      return;
    }

    const updatedValue = moment(value).seconds(0).milliseconds(0);
    const now = moment().subtract(1, 'hours');

    const updatedCode = { ...newCode, [name]: updatedValue.toISOString() };

    const errors: typeof validationErrors = { ...validationErrors };

    if (name === 'scheduled_on') {
      if (updatedValue.isBefore(now.add(10, 'minutes'))) {
        errors.scheduled_on = 'Scheduled time must be at least 10 minutes from now';
      } else {
        errors.scheduled_on = '';
      }

      if (updatedCode.expires_on) {
        const expires = moment(updatedCode.expires_on);
        if (expires.isBefore(updatedValue.clone().add(30, 'minutes'))) {
          errors.expires_on = 'Expiration must be at least 30 minutes after scheduled time';
        } else {
          errors.expires_on = '';
        }
      }
    }

    if (name === 'expires_on') {
      if (updatedCode.scheduled_on) {
        const scheduled = moment(updatedCode.scheduled_on);
        if (updatedValue.isBefore(scheduled.clone().add(30, 'minutes'))) {
          errors.expires_on = 'Expiration must be at least 30 minutes after scheduled time';
        } else {
          errors.expires_on = '';
        }
      }
    }

    setValidationErrors(errors);
    setNewCode(updatedCode);
  };

  const saveNewCode = () => {
    try {
      if (!newCode) {
        return;
      }

      lockScheduledCodeSchema.validateSync(newCode, {
        abortEarly: false,
      });

      setValidationErrors({});

      const existedCode = scheduledCodesArr?.find((item) => item.code === newCode.code);

      if (existedCode) {
        setValidationErrors({
          code: `Code ${newCode.code} already exists as Name ${existedCode.name}`,
        });
        return;
      }

      const slot = getFreeSlot(scheduledCodesArr);
      const newArr = clonedeep(scheduledCodesArr);
      newArr.push({
        slot: parseInt(slot, 10),
        code: newCode.code,
        name: newCode.name,
        scheduled_on: newCode.scheduled_on,
        expires_on: newCode.expires_on,
        is_added: true,
        property_id: property.property_id,
      });

      updateHubScheduledCodes(newArr);

      setNewCode(null);
      setOpenModal(false);
    } catch (errors) {
      setValidationErrors(beautifyErrors(errors));
    }
  };

  return (
    <>
      {scheduledCodesArr?.length > 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%', position: 'relative' }}>
            <Box mb={4}>
              <Typography variant="h6" className={classes.title}>Lock Scheduled Codes</Typography>
            </Box>
            {scheduledCodesArr.map((item) => (
              <Grid
                container
                spacing={3}
                className={classes.codeCard}
                key={item.slot}
              >
                <Grid item xs={11}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box className={classes.cardTime}>
                        <Box className={`${classes.iconWrap} ${classes.codeIcon}`}>
                          <CodeIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Box className={classes.timeLabel}>Code</Box>
                          <Box className={classes.timeValue}>{item.code}</Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box className={classes.cardTime}>
                        <Box className={`${classes.iconWrap} ${classes.nameIcon}`}>
                          <InfoIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Box className={classes.timeLabel}>Name</Box>
                          <Box className={classes.timeValue}>{item.name}</Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box className={classes.cardTime}>
                        <Box className={`${classes.iconWrap} ${classes.scheduledIcon}`}>
                          <CalendarIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Box className={classes.timeLabel}>Scheduled On</Box>
                          <Box className={classes.timeValue}>
                            {formatScheduledDate(item.scheduled_on)}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box className={classes.cardTime}>
                        <Box className={`${classes.iconWrap} ${classes.expiryIcon}`}>
                          <ClockIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Box className={classes.timeLabel}>Expires On</Box>
                          <Box className={classes.timeValue}>
                            {formatScheduledDate(item.expires_on)}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={1}>
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      disabled={!canEdit}
                      size="small"
                      onClick={() => deleteScheduledCode(item.slot)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            ))}
            <Box mt={3} justifyContent="center" display="flex" width="100%">
              {addNewCodeButton()}
            </Box>
          </Paper>
        </Grid>
      )}

      {scheduledCodesArr.length === 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
            >

              <Box className={classes.iconContainer}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6c757d"
                  strokeWidth="1.5"
                  className={classes.icon}
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </Box>

              <Typography variant="h6" gutterBottom>
                No Scheduled Codes Yet
              </Typography>

              <Typography variant="body2">
                Create codes that work only on specific days and times.
                Perfect for guests, cleaners, or deliveries.
              </Typography>

              <Box mt={3} justifyContent="center" display="flex" width="100%">
                {addNewCodeButton()}
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          className: classes.modalWrapper,
        }}
      >
        <Typography variant="h6" gutterBottom className={classes.modalTitle}>New Smart Lock Scheduled Code</Typography>

        <Grid container spacing={2} className={classes.fieldWrapper}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Code"
              variant="outlined"
              size="small"
              margin="none"
              name="code"
              className={classes.textField}
              inputProps={{ maxLength: 4 }}
              required
              disabled={!canEdit}
              value={newCode?.code}
              onChange={handleNewCode}
              error={Boolean(validationErrors.code)}
              helperText={validationErrors.code}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Name"
              variant="outlined"
              size="small"
              margin="none"
              name="name"
              className={classes.textField}
              inputProps={{ maxLength: 16 }}
              required
              disabled={!canEdit}
              value={newCode?.name}
              onChange={handleNewCode}
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />
          </Grid>
          <Grid item xs={6}>
            <ThemeProvider theme={pickerTheme}>
              <MuiPickersUtilsProvider
                utils={MomentUtils}
                locale="en"
              >
                <DateTimePicker
                  disabled={!canEdit}
                  placeholder="Scheduled On"
                  inputVariant="outlined"
                  size="small"
                  value={newCode?.scheduled_on ? moment(newCode.scheduled_on) : null}
                  onChange={(val) => handleDateChange('scheduled_on', val)}
                  required
                  name="scheduled_on"
                  fullWidth
                  error={Boolean(validationErrors.scheduled_on)}
                  helperText={validationErrors.scheduled_on}
                  className={classes.textField}
                />
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </Grid>
          <Grid item xs={6}>
            <ThemeProvider theme={pickerTheme}>
              <MuiPickersUtilsProvider
                utils={MomentUtils}
                locale="en"
              >
                <DateTimePicker
                  disabled={!canEdit}
                  placeholder="Expires On"
                  inputVariant="outlined"
                  value={newCode?.expires_on ? moment(newCode.expires_on) : null}
                  onChange={(val) => handleDateChange('expires_on', val)}
                  size="small"
                  required
                  name="expires_on"
                  fullWidth
                  error={Boolean(validationErrors.expires_on)}
                  helperText={validationErrors.expires_on}
                  className={classes.textField}
                />
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </Grid>
        </Grid>

        <Box className={classes.buttonWrapper}>
          <CustomButton
            disabled={!canEdit}
            variant="orange"
            fullWidth
            onClick={saveNewCode}
          >
            Save
          </CustomButton>

          <CustomButton
            variant="white"
            fullWidth
            onClick={() => {
              setOpenModal(false);
              setNewCode(null);
            }}
          >
            Cancel
          </CustomButton>
        </Box>
      </Dialog>
    </>
  );
};

export default HubScheduledCodesForm;
